import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useDecideUsageAdjustment } from '../queries/usageAdjustments'
import type { AdjustmentDecisionAction, UsageAdjustmentRequest } from '../types/usageAdjustment.types'

interface Props {
  accountId: string
  adjustment: UsageAdjustmentRequest
  action: AdjustmentDecisionAction
  onClose: () => void
}

export function DecisionModal({ accountId, adjustment, action, onClose }: Props) {
  const [reason, setReason] = useState('')
  const mutation = useDecideUsageAdjustment(accountId)

  const isApprove = action === 'approve'
  const isValid = reason.trim().length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    mutation.mutate(
      { id: adjustment.id, input: { action, reason: reason.trim() } },
      { onSuccess: onClose },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isApprove ? 'Approve' : 'Reject'} Adjustment
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isApprove
                ? 'This will fire the usage-adjustment event emission.'
                : 'No usage event will be emitted for this request.'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
            <span className="font-medium text-gray-900">{adjustment.eventCount >= 0 ? '+' : ''}{adjustment.eventCount.toLocaleString()}</span>
            {' '}events, {adjustment.targetPeriodStart} → {adjustment.targetPeriodEnd}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isApprove ? 'Approval' : 'Rejection'} Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isApprove ? 'Why is this adjustment approved?' : 'Why is this adjustment rejected?'}
              maxLength={1000}
              rows={4}
              required
              autoFocus
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant={isApprove ? 'default' : 'destructive'} disabled={!isValid || mutation.isPending}>
              {mutation.isPending ? 'Submitting…' : isApprove ? 'Approve' : 'Reject'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
